import * as React from 'react';
import { Grid, Modal, Tooltip } from '@material-ui/core';
import dataList from '../../constants/dataList';
import MUIDataTable from "mui-datatables";
import Loading from '../../components/loading';
import VisibilityIcon from '@material-ui/icons/Visibility';
import RefreshIcon from '@material-ui/icons/Refresh';
import WorkgroupDetailsModal from './workgroup-details.modal';
import SearchFilter from '../../components/searchFilter';
import { CommonStyles } from '../../hooks/styles';

interface WorkgroupsProps {
	authUser?: any
	workgroups: any[]
	totalWorkgroups: any
	globalRowsPerPage: any
	getWorkgroups: (filter: any) => void
	reprocessWorkgroup: (payload: any) => void
	setGlobalRowsPerPage: (numRows: any) => void
}

export const WorkgroupsComponent: React.FC<WorkgroupsProps> = (props) => {
	const classes = CommonStyles();
	const firstRender = React.useRef(false);
	const [modalOpen, setModalOpen] = React.useState(false)
	const [groupDetails, setGroupDetails] = React.useState<any>(null)
	const [sortOrder, setSortOrder] = React.useState(null as any)
	const [filters, setFilters] = React.useState<any>({})
	const [tableStatePersist, setTableStatePersist] = React.useState(null as any);
	const [pagination, setPageState] = React.useState({
		offset: 0,
		limit: props.globalRowsPerPage,
		page: 0
	});

	const renderIcons: any = (tableMeta: any) => {
		return (
			<div style={{ color: 'rgba(0, 0, 0, 0.54)', float: 'right' }}>
				<Tooltip title="Details" placement="bottom-start">
					<VisibilityIcon
						onClick={(e) => {
							setModalOpen(true)
							setGroupDetails(props.workgroups[tableMeta.rowIndex])
						}}
						style={{ marginRight: 10, cursor: 'pointer' }}
					/>
				</Tooltip>

				{['MANIFEST_ERROR'].includes(props.workgroups[tableMeta.rowIndex].status) &&
					<Tooltip title="Reprocess" placement="bottom-start">
						<RefreshIcon
							onClick={(e) => {
								props.reprocessWorkgroup({
									id: props.workgroups[tableMeta.rowIndex].id,
									success: () => {
										fitlerWorkgroups()
									}
								  })
							}}
							style={{ marginRight: 10, cursor: 'pointer' }}
						/>
					</Tooltip>
				}
			</div>
		)
	}

	const columnData = [
		{
			label: "Workgroup Name",
			name: "name",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "Status",
			name: "status",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "Pack Lane",
			name: "lane",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "",
			name: "",
			change: false,
			viewColumns: false,
			display: true,
			sort: false,
			customBodyRender: (value: any, tableMeta: any) => { return renderIcons(tableMeta) }
		},
	]

	const getTableColumns = () => {
		return columnData.map((column: any, index: number) => {
			return {
				label: column.label,
				name: column.name,
				options: {
					filter: true,
					display: tableStatePersist ? tableStatePersist.columns[index].display : column.display,
					change: column.change,
					sort: column.sort ?? true,
					viewColumns: column.viewColumns,
					...(column.customBodyRender) && { customBodyRender: column.customBodyRender },
				}
			}
		})
	}

	React.useEffect(() => {
		if (props.authUser && !firstRender.current) {
			fitlerWorkgroups()
		}
		else {
			firstRender.current = false;
		}
	}, [pagination, sortOrder])

	const getFilter = (reset?: boolean) => {
		const filter: any = {
			limit: props.globalRowsPerPage,
			offset: pagination.offset,
			order: sortOrder ? [sortOrder] : ['id'],
			where: {} //filters ultimately end up here 
		}

		Object.keys(filters).forEach((filterKey: string) => {
			if(filterKey === 'status' && filters[filterKey]) filter.where[filterKey] = {inq: filters[filterKey].map((filter: any) => filter.id)}
			else filter.where[filterKey] = filters[filterKey]
		})

		return filter
	}

	const fitlerWorkgroups = (reset?: boolean) => {
		props.getWorkgroups({ filter: getFilter(reset) })
	}

	const showLoadingMsg = () => (
		!props.authUser && props.workgroups.length === 0
	)

	const handleModalClose = () => {
		setModalOpen(false)
	};

	React.useEffect(() => {
		const interval = setInterval(() => {
		  if(props.workgroups.some((workgroup: any) => workgroup.status === "PROCESSING")){
			fitlerWorkgroups()
		  }
		}, 5000);
		return () => clearInterval(interval);
	  }, [props.workgroups]);

	return (<div>
		{showLoadingMsg() ? <div><Loading message="" /></div> : <div>
			<div className={classes.searchWrap}>
				<Grid container>
					<Grid item xs={'auto'}>
						<SearchFilter
							config={[
								{
									label: 'Status',
									name: 'status',
									type: 'multiAutoComplete',
									selectOptions: dataList.WorkgroupStatus.map((status: any) => ({ name: status.label, id: status.value })),
									// TODO figure out default values for filters
									// defaultValue: ['READY', 'PROCESSING']
								},
							]}
							onFilterChange={setFilters}
							filters={filters}
							handleSearch={fitlerWorkgroups}
						/>
					</Grid>
				</Grid>
			</div>
			<div>
				<MUIDataTable
					data={props.workgroups}
					columns={getTableColumns()}
					options={{
						rowsPerPage: props.globalRowsPerPage,
						rowsPerPageOptions: dataList.PageLimitOptions,
						onChangeRowsPerPage: (numberOfRows: number) => {
							setPageState({ offset: 0, limit: numberOfRows, page: 0 });
							props.setGlobalRowsPerPage(numberOfRows);
						},
						filterType: "dropdown",
						responsive: "simple",
						filter: false,
						search: false,
						download: true,
						print: true,
						selectableRows: 'none',
						serverSide: true,
						page: pagination.page,
						count: props.totalWorkgroups,
						onDownload: (buildHead: any, buildBody: any, columns: any) => {
							const filter = getFilter()
							delete filter.limit
							delete filter.offset
							const exportOptions = {
								columns: columns,
								timestampFormat: 'YYYY-MM-DD',
								fileName: 'workgroups'
							}
							props.getWorkgroups({ filter: filter, exportOptions: exportOptions })
							return false
						},
						onColumnSortChange: (changedColumn: string, direction: string) => {
							setSortOrder(`${changedColumn} ${direction.toUpperCase()}`)
						},
						onTableChange: (tableAction: any, tableState: any) => {
							switch (tableAction) {
								case 'changePage':
									if (tableState.page > pagination.page) {
										setPageState({ offset: pagination.offset + props.globalRowsPerPage, limit: props.globalRowsPerPage, page: tableState.page });
									}
									else if (tableState.page < pagination.page) {
										setPageState({ offset: pagination.offset - props.globalRowsPerPage, limit: props.globalRowsPerPage, page: tableState.page });
									}
									break;
								case 'viewColumnsChange':
									setTableStatePersist(tableState);
									break;
							}
						},
					}}
				/>
			</div>
			{/* :
				<EmptyContent message="Change the filter criteria above to view product information." />
			} */}
			<Modal
				open={modalOpen}
				onClose={(e: any, reason: string) => {
					if (reason === 'backdropClick') return
					handleModalClose()
				}}
				aria-labelledby="simple-modal-title"
				aria-describedby="simple-modal-description"
			>
				<>
					<div>
						<WorkgroupDetailsModal closeModal={handleModalClose} workgroup={groupDetails} getWorkgroups={() => props.getWorkgroups({ filter: getFilter(false) })} />
					</div>
				</>
			</Modal>
		</div>}
	</div>)
}